import React from 'react'
import { useNavigate } from 'react-router-dom'
import SafeIcon from '../common/SafeIcon'
import { FiArrowLeft } from 'react-icons/fi'

const PrivacyPolicy = () => {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            <span>Back</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
          
          <div className="text-sm text-gray-600 mb-8">
            <p><strong>Effective Date:</strong> 21/08/25</p>
            <p><strong>Last Updated:</strong> 21/08/25</p>
          </div>

          <div className="prose prose-gray max-w-none space-y-6">
            <p>
              This Privacy Policy explains how Nu Pitch Ltd. ("NuPitch", "we", "us", or "our") collects, uses, shares, and protects your personal data when you use our services available via <a href="https://usenupitch.com" className="text-blue-600 hover:text-blue-800">https://usenupitch.com</a> and <a href="https://deck.usenupitch.com" className="text-blue-600 hover:text-blue-800">https://deck.usenupitch.com</a> (the "Service").
            </p>
            
            <p>
              Nu Pitch Ltd is a company registered in Northern Ireland under Company Number NI697170, with its registered office at 4 Church Brae, Altnagevlin, BT47 3QG.
            </p>
            
            <p>
              For any privacy-related queries, you can contact us at: <a href="mailto:support@usenupitch.com" className="text-blue-600 hover:text-blue-800">support@usenupitch.com</a>.
            </p>
            
            <p>
              We are committed to complying with the UK Data Protection Act 2018 and the General Data Protection Regulation (GDPR) (EU) 2016/679.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">1. Information We Collect</h2>
            <p>When you use our Service, we may collect the following categories of personal data:</p>

            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">1.1 Information You Provide Directly</h3>
            <ul className="list-disc list-inside space-y-2">
              <li>Account registration details (name, email address, password).</li>
              <li>Profile information (optional): biography, social media links, contact details, videos, or portfolio links.</li>
              <li>Uploaded or generated content (scripts, text, images, video, slide decks).</li>
              <li>Payment information (processed by third-party providers; we do not store full card details).</li>
              <li>Customer support communications.</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">1.2 Information We Collect Automatically</h3>
            <ul className="list-disc list-inside space-y-2">
              <li>Usage data: pages visited, features used, time spent on the Service.</li>
              <li>Device and browser data: IP address, operating system, device type, cookies, and similar technologies.</li>
              <li>Log data for security and troubleshooting.</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">1.3 Information from Third-Party Services</h3>
            <p>When you use AI features, your data (prompts, scripts, images, or text inputs) may be transmitted to third-party AI providers, such as:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>fal.ai (image generation)</li>
              <li>OpenRouter (text generation)</li>
            </ul>
            <p>These providers process your input to generate outputs. Their own privacy policies apply.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">2. How We Use Your Information</h2>
            <p>We process personal data for the following purposes:</p>
            <ul className="list-disc list-inside space-y-2">
              <li><strong>Account Management:</strong> To create and manage your user account.</li>
              <li><strong>Service Delivery:</strong> To provide slide deck generation, editing, publishing, and sharing features.</li>
              <li><strong>AI Processing:</strong> To send your input to third-party AI providers for text and image generation.</li>
              <li><strong>Payments:</strong> To process subscription or transaction payments.</li>
              <li><strong>Security:</strong> To maintain the safety and integrity of the Service.</li>
              <li><strong>Communication:</strong> To respond to support requests and send important notices.</li>
              <li><strong>Improvement:</strong> To analyze usage patterns and improve features.</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">3. Legal Bases for Processing (GDPR)</h2>
            <p>We process personal data under the following lawful bases:</p>
            <ul className="list-disc list-inside space-y-2">
              <li><strong>Contractual necessity:</strong> To provide and operate the Service you have requested.</li>
              <li><strong>Legitimate interests:</strong> To improve our Service, prevent fraud, and ensure security.</li>
              <li><strong>Consent:</strong> For optional profile information, marketing communications, and certain cookies.</li>
              <li><strong>Legal obligation:</strong> To comply with accounting, taxation, or other legal requirements.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">4. Sharing of Information</h2>
            <p>We may share your personal data with:</p>
            <ul className="list-disc list-inside space-y-2">
              <li><strong>Service Providers:</strong> Such as Supabase (database hosting), payment processors, and cloud infrastructure providers.</li>
              <li><strong>AI Providers:</strong> fal.ai and OpenRouter for content generation.</li>
              <li><strong>Legal Authorities:</strong> Where required by law, regulation, or valid legal request.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets.</li>
            </ul>
            <p>We do not sell or rent your personal data.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">5. International Data Transfers</h2>
            <p>As part of the Service, your data may be transferred outside the UK or EEA, including to the United States.</p>
            <p>When we transfer personal data, we ensure appropriate safeguards such as Standard Contractual Clauses (SCCs) or equivalent legal mechanisms are in place with third-party processors.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">6. Data Security</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>We use Supabase as our primary database infrastructure.</li>
              <li>We apply encryption, access controls, and security monitoring to protect your information.</li>
              <li>However, no online system is completely secure. By using the Service, you acknowledge that uploading data online carries inherent risks of unauthorized access or disclosure.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">7. Data Retention</h2>
            <p>We retain personal data only for as long as necessary for the purposes set out in this Policy:</p>
            <ul className="list-disc list-inside space-y-2">
              <li><strong>Account data:</strong> retained while your account is active.</li>
              <li><strong>Generated content:</strong> retained as long as you keep it stored or published on our Service.</li>
              <li><strong>Payment and transaction records:</strong> retained as required by law (typically 6 years for accounting).</li>
              <li><strong>Support correspondence:</strong> retained up to 2 years.</li>
            </ul>
            <p>You may request deletion of your account and associated data at any time (see Section 9).</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">8. Cookies and Tracking Technologies</h2>
            <p>We use cookies and similar technologies to:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>Enable essential Service functionality.</li>
              <li>Remember preferences.</li>
              <li>Analyze Service usage.</li>
            </ul>
            <p>You can manage or disable cookies in your browser settings. Some features may not function properly if cookies are disabled.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">9. Your Rights (GDPR and UK GDPR)</h2>
            <p>As a data subject, you have the following rights:</p>
            <ul className="list-disc list-inside space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data.</li>
              <li><strong>Rectification:</strong> Correct inaccurate or incomplete data.</li>
              <li><strong>Erasure ("Right to be Forgotten"):</strong> Request deletion of your personal data.</li>
              <li><strong>Restriction:</strong> Limit how we process your data.</li>
              <li><strong>Portability:</strong> Request your data in a machine-readable format.</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interests.</li>
              <li><strong>Withdrawal of Consent:</strong> Where consent is the lawful basis, you may withdraw it at any time.</li>
            </ul>
            <p>To exercise your rights, contact us at <a href="mailto:support@usenupitch.com" className="text-blue-600 hover:text-blue-800">support@usenupitch.com</a>.</p>
            <p>You also have the right to lodge a complaint with your local Data Protection Authority, including the Information Commissioner's Office (ICO) in the UK.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">10. Children's Privacy</h2>
            <p>The Service is not directed at children under the age of 18. We do not knowingly collect personal data from children. If you believe a child has provided us with personal data, please contact us to request deletion.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">11. Public Sharing Disclaimer</h2>
            <p>If you choose to publish slide decks or create a public profile, the information you share will be accessible to anyone on the internet. You are solely responsible for the content you make public.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">12. Changes to this Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time. The updated version will be posted on our website with a new "Last Updated" date. If changes are material, we will notify you by email or within the Service.</p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">13. Contact Information</h2>
            <p>If you have any questions or concerns about this Privacy Policy, please contact us:</p>
            <div className="ml-4 space-y-1">
              <p>Nu Pitch Ltd</p>
              <p>Registered Office: 4 Church Brae, Altnagevlin, BT47 3QG</p>
              <p>Email: <a href="mailto:support@usenupitch.com" className="text-blue-600 hover:text-blue-800">support@usenupitch.com</a></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy
